import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { DEFAULT_PAYG_PLAN, PAYG_VALIDATION } from "../../../constants";
import FormGroup from "./FormGroup";
import './PricingConfig.css';

/**
 * Default Pay-As-You-Go prices for newly created companies.
 *
 * A separate modal from EditPricingModal rather than another tab inside it: the two forms
 * share almost no fields, and putting a per-SEAT price and a per-TOPO price in one form is
 * exactly where the `pricingPlan.topo` / `paygPlan.pricePerTopo` naming collision would
 * bite a Super Admin.
 *
 * Posts only the `payg` block. The API merges it into the existing pricing config, so
 * saving here cannot wipe the seat prices - and saving the seat form cannot wipe these.
 */
const EditPaygPricingModal = ({ activeModal, setActiveModal, setUpdateModal, currentConfig }) => {
  const [formData, setFormData] = useState({ ...DEFAULT_PAYG_PLAN });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef({});

  // The field components are re-created on each render, so the caret would jump to the end
  // of the input on every keystroke without this. Same pattern as the seat pricing form.
  const setInputRef = useCallback((name) => (element) => {
    if (element) {
      inputRefs.current[name] = element;
    } else {
      delete inputRefs.current[name];
    }
  }, []);

  useEffect(() => {
    if (activeModal) {
      setFormData({ ...DEFAULT_PAYG_PLAN, ...(currentConfig?.payg || {}) });
      setErrors({});
    }
  }, [currentConfig, activeModal]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type } = e.target;

    const selectionStart = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;

    setFormData((previous) => ({
      ...previous,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));

    setErrors((previous) => (previous[name] ? { ...previous, [name]: undefined } : previous));

    // Restore the caret after React re-renders the input.
    requestAnimationFrame(() => {
      const element = inputRefs.current[name];
      if (element && type !== 'number' && selectionStart !== null) {
        element.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};

    const base = Number(formData.baseCharge);
    if (!Number.isFinite(base) || base < PAYG_VALIDATION.MIN_BASE_CHARGE || base > PAYG_VALIDATION.MAX_BASE_CHARGE) {
      newErrors.baseCharge = `Base price must be between ${PAYG_VALIDATION.MIN_BASE_CHARGE} and ${PAYG_VALIDATION.MAX_BASE_CHARGE}`;
    }

    const perTopo = Number(formData.pricePerTopo);
    if (!Number.isFinite(perTopo) || perTopo < PAYG_VALIDATION.MIN_PRICE_PER_TOPO || perTopo > PAYG_VALIDATION.MAX_PRICE_PER_TOPO) {
      newErrors.pricePerTopo = `Per topo charge must be between ${PAYG_VALIDATION.MIN_PRICE_PER_TOPO} and ${PAYG_VALIDATION.MAX_PRICE_PER_TOPO}`;
    }

    const day = Number(formData.invoiceDayOfMonth);
    if (!Number.isInteger(day) || day < PAYG_VALIDATION.MIN_INVOICE_DAY || day > PAYG_VALIDATION.MAX_INVOICE_DAY) {
      newErrors.invoiceDayOfMonth = `Invoice day must be a whole number between ${PAYG_VALIDATION.MIN_INVOICE_DAY} and ${PAYG_VALIDATION.MAX_INVOICE_DAY}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    setIsLoading(true);

    try {
      const myurl = `${process.env.REACT_APP_base_url}api/system-config/default-pricing`;

      // Only the `payg` block. The API merges it, leaving the seat prices untouched.
      const response = await axios.post(
        myurl,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          masterAdminId: sessionStorage.getItem("TES_Admin_ID"),
          payg: {
            baseCharge: Number(formData.baseCharge) || 0,
            pricePerTopo: Number(formData.pricePerTopo) || 0,
            currency: 'usd',
            invoiceDayOfMonth: Number(formData.invoiceDayOfMonth) || 1,
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success === true) {
        toast.success("Pay As You Go pricing updated successfully!");
        setActiveModal();
        setUpdateModal();
      } else {
        const serverErrors = response.data?.data?.errors;
        if (Array.isArray(serverErrors) && serverErrors.length) {
          toast.error(serverErrors.join('. '));
        } else {
          toast.error(response.data.message || "Failed to update Pay As You Go pricing");
        }
      }
    } catch (error) {
      console.error("Error updating PAYG pricing:", error);
      toast.error(error.response?.data?.message || "Failed to update Pay As You Go pricing");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveModal();
    setErrors({});
  };

  const exampleTopos = 20;
  const exampleUsage = (Number(formData.pricePerTopo) || 0) * exampleTopos;
  const exampleTotal = exampleUsage + (Number(formData.baseCharge) || 0);
  const money = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount) || 0);

  // Bound to this form's state; the markup itself lives in the shared FormGroup so this
  // modal and the Seat modal cannot drift apart.
  const fieldProps = (name) => ({
    name,
    value: formData[name],
    error: errors[name],
    onChange: handleInputChange,
    inputRef: setInputRef(name),
    step: "0.01",
    min: PAYG_VALIDATION[name === 'baseCharge' ? 'MIN_BASE_CHARGE' : 'MIN_PRICE_PER_TOPO'],
    max: PAYG_VALIDATION[name === 'baseCharge' ? 'MAX_BASE_CHARGE' : 'MAX_PRICE_PER_TOPO'],
  });

  return (
    <Modal
      show={activeModal}
      onHide={handleClose}
      size="xl"
      backdrop="static"
      centered
      className="pricing-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-dollar-sign me-2 mr-2"></i>
          Edit Pay As You Go Pricing
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row">
            {/* Monthly Base Section */}
            <div className="col-12">
              <div className="form-section">
                <h5 className="form-section-title">
                  <i className="fa fa-building form-section-icon"></i>
                  Monthly Base
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup
                      label="Base Monthly Charge"
                      helpText="Fixed monthly charge (USD, may be 0), billed for the month ahead"
                      icon="fa-home"
                      {...fieldProps('baseCharge')}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="currency" className="form-label">
                        <i className="fa fa-globe me-2 mr-2"></i>
                        Currency
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="currency"
                        name="currency"
                        value="usd"
                        required
                        disabled
                      >
                        <option value="usd">usd - US Dollar ($)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-Topo Pricing Section */}
            <div className="col-12">
              <div className="form-section">
                <h5 className="form-section-title">
                  <i className="fa fa-map form-section-icon"></i>
                  Per-Topo Pricing
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup
                      label="Per Topo Charge"
                      helpText="Cost (USD, may be 0) for each topo created, billed for the month just closed"
                      icon="fa-map"
                      {...fieldProps('pricePerTopo')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Invoicing Section */}
            <div className="col-12">
              <div className="form-section">
                <h5 className="form-section-title">
                  <i className="fa fa-file-invoice form-section-icon"></i>
                  Invoicing
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="invoiceDayOfMonth" className="form-label">
                        <i className="fa fa-calendar-alt me-2 mr-2"></i>
                        Invoice Day
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <select
                        className={`form-control ${errors.invoiceDayOfMonth ? 'is-invalid' : ''}`}
                        id="invoiceDayOfMonth"
                        name="invoiceDayOfMonth"
                        value={formData.invoiceDayOfMonth}
                        onChange={handleInputChange}
                        required
                      >
                        {[1, 2, 3, 4, 5].map((day) => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                      <div className="form-text">Day of the month following the usage on which the invoice is sent</div>
                      {errors.invoiceDayOfMonth && <div className="invalid-feedback d-block">{errors.invoiceDayOfMonth}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Live Preview Section */}
            <div className="col-12">
              <div className="pricing-preview">
                <div className="preview-title">
                  <i className="fa fa-eye"></i>
                  Live Preview - Example Invoice
                </div>
                <div className="preview-content">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="cost-breakdown">
                        {Number(formData.baseCharge) > 0 && (
                          <div className="cost-item">
                            <span className="cost-item-label">Monthly base (September):</span>
                            <span className="cost-item-value">{money(formData.baseCharge)}</span>
                          </div>
                        )}
                        <div className="cost-item">
                          <span className="cost-item-label">
                            Topo usage - August ({exampleTopos} x {money(formData.pricePerTopo)}):
                          </span>
                          <span className="cost-item-value">{money(exampleUsage)}</span>
                        </div>
                        <div className="cost-item">
                          <span className="cost-item-label">Total:</span>
                          <span className="cost-item-value cost-total">{money(exampleTotal)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="alert alert-info mb-0">
                        <strong>Preview Note:</strong> A company creating {exampleTopos} topos in August is
                        invoiced on day {formData.invoiceDayOfMonth} of September. The base fee covers the
                        month <em>ahead</em>; usage covers the month just closed. Users are unlimited and
                        every add-on is included.
                        {exampleTotal === 0 && (
                          <> A $0 total sends nothing to Stripe; the period is recorded as "No charge".</>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            <i className="fa fa-times me-2 mr-2"></i>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fa fa-spinner fa-spin me-2 mr-2"></i>
                Saving Configuration...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2 mr-2"></i>
                Save Configuration
              </>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditPaygPricingModal;
